import React from 'react';
import type { AspectRatioOption, BaseCropComponentProps } from '../types';
import { useCropStore } from '../store/cropStore';
import { aspectControlsContainer, aspectLabel } from '../styles/index';

interface CropAspectControlsProps extends BaseCropComponentProps {
  label?: string;
  aspectRatioLabels?: {
    square?: string;
    landscape?: string;
    portrait?: string;
    original?: string;
  };
}

export const CropAspectControls: React.FC<CropAspectControlsProps> = ({
  label = 'Aspect Ratio:',
  aspectRatioLabels = {},
  className,
}) => {
  const { aspectRatio, setAspectRatio } = useCropStore();

  const aspectOptions: AspectRatioOption[] = ['original', '1:1', '16:9', '9:16'];

  const getLabel = (option: AspectRatioOption): string => {
    const defaultLabels = {
      '1:1': 'Square',
      '16:9': 'Landscape',
      '9:16': 'Portrait',
      original: 'Original',
    };

    const customLabels = {
      '1:1': aspectRatioLabels.square,
      '16:9': aspectRatioLabels.landscape,
      '9:16': aspectRatioLabels.portrait,
      original: aspectRatioLabels.original,
    };

    return customLabels[option] || defaultLabels[option];
  };

  const getButtonClasses = (isActive: boolean): string => {
    const baseClasses =
      'px-3 py-1.5 text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900';

    if (isActive) {
      return `${baseClasses} bg-blue-600 text-white hover:bg-blue-700`;
    }

    return `${baseClasses} bg-gray-700 text-gray-300 hover:bg-gray-600`;
  };

  return (
    <div className={`${aspectControlsContainer} ${className || ''}`}>
      <span className={`${aspectLabel} hidden sm:inline`}>{label}</span>
      <div className="inline-flex rounded-md shadow-sm w-full sm:w-auto">
        {aspectOptions.map((option, index) => (
          <button
            key={option}
            className={`${getButtonClasses(aspectRatio === option)} ${
              index === 0 ? 'rounded-l-md' : ''
            } ${index === aspectOptions.length - 1 ? 'rounded-r-md' : ''} ${
              index !== 0 ? '-ml-px' : ''
            } flex-1`}
            onClick={() => setAspectRatio(option)}
            aria-pressed={aspectRatio === option}
          >
            {getLabel(option)}
          </button>
        ))}
      </div>
    </div>
  );
};
